import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const LoanDisbursementRecovery = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState(0); // 0 = disbursed, 1 = recovered

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!startDate || !endDate) {
        setError('Please select both start date and end date');
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before or equal to end date');
        return;
      }
      
      const response = await api.get(`/admin/reports/disbursement-recovery?startDate=${startDate}&endDate=${endDate}`);
      setReportData(response.data);
    } catch (err) {
      console.error('Error fetching disbursement recovery report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load report');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!startDate || !endDate) {
        setError('Please select both start date and end date');
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before or equal to end date');
        return;
      }
      
      const response = await api.get(`/admin/reports/disbursement-recovery/pdf?startDate=${startDate}&endDate=${endDate}`, {
        responseType: 'blob',
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateRangeStr = startDate === endDate 
        ? startDate 
        : `${startDate}_to_${endDate}`;
      link.download = `Loan_Disbursement_Recovery_Report_${dateRangeStr}.pdf`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(err.response?.data?.error || err.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Loan Disbursement & Recovery Report
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PdfIcon />}
          onClick={downloadPdf}
          disabled={loading || !reportData}
          sx={{ ml: 2 }}
        >
          Download PDF
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={fetchReport}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && !reportData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : reportData ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">Loans Disbursed</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {reportData.summary?.totalDisbursedLoans || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatCurrency(reportData.summary?.totalDisbursedAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingDownIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">Loans Recovered</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {reportData.summary?.totalRecoveredLoans || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatCurrency(reportData.summary?.totalRecoveredAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalanceIcon sx={{ mr: 1 }} />
                    <Typography variant="body2">Net Disbursement</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(reportData.summary?.netDisbursement)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Disbursed - Recovered
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2">Recovery Rate</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Number(reportData.summary?.recoveryRate || 0).toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Percentage of loans recovered
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Loan Disbursement & Recovery Report ({new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()})
          </Typography>

          {/* Tabs for Disbursed and Recovered */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={activeTab === 0 ? 'contained' : 'outlined'}
                onClick={() => setActiveTab(0)}
                startIcon={<TrendingUpIcon />}
              >
                Disbursed Loans ({reportData.disbursedLoans?.length || 0})
              </Button>
              <Button
                variant={activeTab === 1 ? 'contained' : 'outlined'}
                onClick={() => setActiveTab(1)}
                startIcon={<TrendingDownIcon />}
              >
                Recovered Loans ({reportData.recoveredLoans?.length || 0})
              </Button>
            </Box>
          </Box>

          {/* Disbursed Loans Table */}
          {activeTab === 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Loan Number
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Customer Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                      Loan Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Disbursement Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Officer
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Area
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Loan Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.disbursedLoans && reportData.disbursedLoans.length > 0 ? (
                    reportData.disbursedLoans.map((loan, index) => (
                      <TableRow key={loan.loanId || index} hover>
                        <TableCell>{loan.loanNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {loan.customerName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            NIC: {loan.customerNic}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {loan.customerAddress}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {loan.customerContactNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(loan.loanAmount)}
                        </TableCell>
                        <TableCell>
                          {formatDate(loan.disbursementDate)}
                        </TableCell>
                        <TableCell>{loan.officerName}</TableCell>
                        <TableCell>{loan.areaName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={loan.loanType?.replace('_', ' ') || 'N/A'} 
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">No loans disbursed in this month</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {reportData.disbursedLoans && reportData.disbursedLoans.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          reportData.disbursedLoans.reduce((sum, loan) => 
                            sum + (Number(loan.loanAmount) || 0), 0
                          )
                        )}
                      </TableCell>
                      <TableCell colSpan={4}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Recovered Loans Table */}
          {activeTab === 1 && (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Loan Number
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Customer Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                      Loan Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                      Recovered Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                      Due Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Recovery Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Officer
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      Area
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.recoveredLoans && reportData.recoveredLoans.length > 0 ? (
                    reportData.recoveredLoans.map((loan, index) => (
                      <TableRow key={loan.loanId || index} hover>
                        <TableCell>{loan.loanNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {loan.customerName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            NIC: {loan.customerNic}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {loan.customerAddress}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {loan.customerContactNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(loan.loanAmount)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                            {formatCurrency(loan.recoveredAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: loan.dueAmount > 0 ? 'error.main' : 'success.main', fontWeight: 'medium' }}>
                            {formatCurrency(loan.dueAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(loan.closedDate)}
                        </TableCell>
                        <TableCell>{loan.officerName}</TableCell>
                        <TableCell>{loan.areaName}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">No loans recovered in this month</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {reportData.recoveredLoans && reportData.recoveredLoans.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        TOTAL
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          reportData.recoveredLoans.reduce((sum, loan) => 
                            sum + (Number(loan.loanAmount) || 0), 0
                          )
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          reportData.recoveredLoans.reduce((sum, loan) => 
                            sum + (Number(loan.recoveredAmount) || 0), 0
                          )
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(
                          reportData.recoveredLoans.reduce((sum, loan) => 
                            sum + (Number(loan.dueAmount) || 0), 0
                          )
                        )}
                      </TableCell>
                      <TableCell colSpan={3}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Alert severity="info">
          Select a date range (From Date and To Date), then click "Generate Report" to view the loan disbursement and recovery report.
        </Alert>
      )}
    </Box>
  );
};

export default LoanDisbursementRecovery;

